import { type UseGetFacilityProfileResponse } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useToast } from "@src/appV2/lib";
import { useState } from "react";

import { useUpdateEarlyClockIn } from "../../api/useUpdateEarlyClockIn";
import { useBreakPolicies } from "../../hooks/useBreakPolicies";
import { useHostedTime } from "../../hooks/useHostedTime";
import { useNfcTags } from "../../hooks/useNfcTags";
import { type SettingsPermissions } from "../../types";
import { TimeAttendanceSectionView } from "./TimeAttendanceSectionView";
import { useTu } from "@src/appV2/i18n/useTu";

interface TimeAttendanceSectionContainerProps {
  facilityProfile: UseGetFacilityProfileResponse | undefined;
  guardSettingsAction: (action: () => void) => void;
  permissions: SettingsPermissions;
  workplaceId: string;
  workplaceName: string;
  onSettingsChanged: () => void;
}

export function TimeAttendanceSectionContainer({
  facilityProfile,
  guardSettingsAction,
  permissions,
  workplaceId,
  workplaceName,
  onSettingsChanged,
}: TimeAttendanceSectionContainerProps) {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const isRestrictOverworkFlagEnabled = useCbhFlag(
    CbhFeatureFlag.RESTRICT_OVERWORK_INVITES_TOGGLE,
    { defaultValue: false }
  );

  const isFacilityOrCbhAdmin = permissions.isFacilityAdmin || permissions.isAdmin;

  const [preventEarlyClockIn, setPreventEarlyClockIn] = useState(
    !(facilityProfile?.isEarlyClockInEnabled ?? true)
  );
  const [restrictOverwork, setRestrictOverwork] = useState(
    facilityProfile?.restrictOverworkShiftInvites ?? false
  );
  const [autoVerifyThresholdInMinutes, setAutoVerifyThresholdInMinutes] = useState(
    facilityProfile?.autoVerifyThresholdInMinutes ?? 0
  );

  const { isLoading: isEarlyClockInUpdating, mutateAsync: updateEarlyClockIn } =
    useUpdateEarlyClockIn();
  const { isLoading: isFacilityProfileUpdating, mutateAsync: updateFacilityProfile } =
    useUpdateFacilityProfile();

  const breakPolicies = useBreakPolicies({
    facilityProfile,
    permissions,
    workplaceId,
    onSettingsChanged,
  });

  const hostedTime = useHostedTime({
    facilityId: workplaceId,
    facilityName: workplaceName,
    isCbhAdmin: permissions.isAdmin,
  });

  const nfcTags = useNfcTags({ workplaceId });

  const isUpdating =
    isEarlyClockInUpdating || isFacilityProfileUpdating || breakPolicies.isBreakUpdating;

  const handlePreventEarlyClockInToggle = async (checked: boolean) => {
    setPreventEarlyClockIn(checked);
    const willEarlyClockInBeEnabled = !checked;
    const messageVerb = willEarlyClockInBeEnabled ? "enabled" : "disabled";

    try {
      await updateEarlyClockIn({
        facilityId: workplaceId,
        isEarlyClockInEnabled: willEarlyClockInBeEnabled,
      });
      showSuccessToast(`Early clock-in ${messageVerb} successfully.`);
      onSettingsChanged();
    } catch {
      showErrorToast(
        `Couldn't ${willEarlyClockInBeEnabled ? "enable" : "disable"} early clock-in.`
      );
      setPreventEarlyClockIn(!checked);
    }
  };

  const handleRestrictOverworkToggle = async (checked: boolean) => {
    setRestrictOverwork(checked);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        restrictOverworkShiftInvites: checked,
      });
      showSuccessToast(tu("Setting updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update setting"));
      setRestrictOverwork(!checked);
    }
  };

  const handleAutoVerifyThresholdChange = async (value: number) => {
    const previousValue = autoVerifyThresholdInMinutes;
    setAutoVerifyThresholdInMinutes(value);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        autoVerifyThresholdInMinutes: value,
      });
      showSuccessToast(tu("Setting updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update setting"));
      setAutoVerifyThresholdInMinutes(previousValue);
    }
  };

  return (
    <TimeAttendanceSectionView
      autoDeclinationReason={breakPolicies.autoDeclinationReason}
      autoDeclineReasonModalState={breakPolicies.autoDeclineReasonModalState}
      autoVerifyThresholdInMinutes={autoVerifyThresholdInMinutes}
      breakPolicies={breakPolicies.breakPolicies}
      hostedTimeDeviceId={hostedTime.deviceId}
      nfcRequestList={nfcTags.nfcRequestList}
      isAutoDeclineVisible={breakPolicies.isAutoDeclineVisible}
      isAutoDeclinationEnabled={breakPolicies.isAutoDeclinationEnabled}
      isAutoVerifyVisible={permissions.isAdmin}
      isCaliforniaFacility={breakPolicies.isCaliforniaFacility}
      isHostedTimeLoading={hostedTime.isLoading}
      isHostedTimeUpdating={hostedTime.isUpdating}
      isRestrictOverworkVisible={isRestrictOverworkFlagEnabled && isFacilityOrCbhAdmin}
      isUpdating={isUpdating}
      mandatoryBreakPolicyModalState={breakPolicies.mandatoryBreakPolicyModalState}
      mandatoryBreakPolicyText={breakPolicies.mandatoryBreakPolicyText}
      permissions={permissions}
      preventEarlyClockIn={preventEarlyClockIn}
      providesRestBreaks={breakPolicies.providesRestBreaks}
      requiresLunchBreak={breakPolicies.requiresLunchBreak}
      restBreakPolicyModalState={breakPolicies.restBreakPolicyModalState}
      restBreakPolicyText={breakPolicies.restBreakPolicyText}
      restrictOverwork={restrictOverwork}
      workplaceId={workplaceId}
      onAutoDeclineReasonSave={breakPolicies.handleAutoDeclineReasonSave}
      onBreakPolicySave={breakPolicies.handleBreakPolicySave}
      onDeleteNfcTag={nfcTags.onDeleteNfcTag}
      onRequestNfcTags={nfcTags.onRequestNfcTags}
      onSaveNfcTagLocation={nfcTags.onSaveNfcTagLocation}
      onAutoDeclineToggle={(checked) => {
        guardSettingsAction(() => {
          void breakPolicies.handleAutoDeclineToggle(checked);
        });
      }}
      onHostedTimeDeviceIdChange={hostedTime.onDeviceIdChange}
      onHostedTimeSave={() => {
        guardSettingsAction(hostedTime.onSave);
      }}
      onMandatoryBreakPolicySave={breakPolicies.handleMandatoryBreakPolicySave}
      onMandatoryBreakToggle={(checked) => {
        guardSettingsAction(() => {
          void breakPolicies.handleMandatoryBreakToggle(checked);
        });
      }}
      onPreventEarlyClockInToggle={(checked) => {
        guardSettingsAction(() => {
          void handlePreventEarlyClockInToggle(checked);
        });
      }}
      onRestBreakPolicySave={breakPolicies.handleRestBreakPolicySave}
      onRestBreaksToggle={(checked) => {
        guardSettingsAction(() => {
          void breakPolicies.handleRestBreaksToggle(checked);
        });
      }}
      onRestrictOverworkToggle={(checked) => {
        guardSettingsAction(() => {
          void handleRestrictOverworkToggle(checked);
        });
      }}
      onAutoVerifyThresholdChange={(value) => {
        guardSettingsAction(() => {
          void handleAutoVerifyThresholdChange(value);
        });
      }}
    />
  );
}
